import { bopApi } from '@/lib/api/client';

const PATH = 'v1/payout-accounts';

/** Target states offered by the edit dialog; the full lifecycle may contain more read-only states. */
export const PAYOUT_ACCOUNT_STATE_CHOICES = ['CREATED', 'DEACTIVATED', 'PENDING'] as const;
export type PayoutAccountStateChoice = (typeof PAYOUT_ACCOUNT_STATE_CHOICES)[number];

/**
 * Raw snake_case record from funds, passed through verbatim by bop-api. Fiat and
 * crypto accounts populate different subsets (iban/bic vs blockchain_address) and
 * records may include more fields than listed here.
 */
export interface PayoutAccount {
  payout_account_id: string;
  currency: string;
  /** PID uuid of the owning user */
  account_holder: string;
  account_holder_name?: string;
  type?: string;
  state: string;
  /** ISO datetime — the client sorts by this, desc (legacy BFF behaviour) */
  created: string;
  iban?: string;
  bic?: string;
  blinc_id?: string;
  address?: string;
  country_code?: string;
  blockchain_address?: string;
  label?: string;
  [additionalData: string]: unknown;
}

/** funds envelope — `error` can appear on a funds-level failure that is still HTTP 200. */
interface GetPayoutAccountsResponse {
  success: boolean;
  data?: {
    payoutAccounts?: PayoutAccount[];
  };
  error?: unknown;
}

export interface GetPayoutAccountsParams {
  /** Both dates or neither — bop-api only applies the created range when BOTH are present. */
  fromDate?: Date;
  toDate?: Date;
  /** Restricts the list to one user (user-console embedding). */
  accountHolderId?: string;
}

// Wire errors are not always strings — the update 500 body's `error` is the WHOLE
// funds response object, so stringify defensively for display.
const stringifyWireError = (error: unknown): string | null => {
  if (error === null || error === undefined) return null;
  if (typeof error === 'string') return error;
  return JSON.stringify(error);
};

export const getPayoutAccounts = async ({ fromDate, toDate, accountHolderId }: GetPayoutAccountsParams = {}): Promise<PayoutAccount[]> => {
  const searchParams = new URLSearchParams();
  // Preserve: a single date without the other is ignored server-side — send both or neither.
  if (fromDate && toDate) {
    searchParams.set('fromDate', fromDate.toISOString());
    searchParams.set('toDate', toDate.toISOString());
  }
  if (accountHolderId) searchParams.set('accountHolderId', accountHolderId);
  const response = await bopApi.get(PATH, { searchParams }).json<GetPayoutAccountsResponse>();
  // Fix: legacy sorted result.data.payoutAccounts unguarded — a funds-level
  // failure (success:false inside an HTTP 200) TypeError'd into a blank page.
  const payoutAccounts = response.data?.payoutAccounts;
  if (response.success !== true || !Array.isArray(payoutAccounts)) {
    throw new Error(stringifyWireError(response.error) ?? 'Funds returned an unsuccessful payout-accounts response');
  }
  return payoutAccounts;
};

// The Nuxt BFF sorted by `created` desc before returning — funds order is
// not guaranteed, so the client must re-sort after fetching.
export const sortPayoutAccountsByCreatedDesc = (accounts: PayoutAccount[]): PayoutAccount[] => [...accounts].sort((a, b) => Date.parse(b.created) - Date.parse(a.created));

export interface UpdatePayoutAccountRequest {
  /**
   * Uppercase on the wire. Audit-trail only — never forwarded to funds, but the
   * request fails without it (`PID_REQUIRED`).
   */
  PID: string;
  payoutAccountId: string;
  newState: string;
  /** Audit justification (35–500 chars). Audit-trail only, required (`AUDIT_COMMENT_REQUIRED`). */
  comment: string;
}

const parseJsonSafely = (raw: string): unknown => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const extractWireError = (body: unknown): string | null => {
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  return stringifyWireError((body as { error: unknown }).error);
};

const isSuccessEnvelope = (body: unknown): boolean => typeof body === 'object' && body !== null && 'success' in body && (body as { success: unknown }).success === true;

/**
 * Fix: the legacy Nuxt route ignored the response entirely, so every state
 * change looked successful. The response is handled by hand here: non-2xx is a
 * failure (its 500 `error` can be a whole object — stringified defensively), and a
 * 200 body can still carry funds `success: false`.
 */
export const updatePayoutAccount = async (body: UpdatePayoutAccountRequest): Promise<void> => {
  const response = await bopApi.post(PATH, { json: body, throwHttpErrors: false });
  const parsed = parseJsonSafely(await response.text());
  const wireError = extractWireError(parsed);
  if (!response.ok) {
    throw new Error(wireError ?? `Payout account update failed (HTTP ${response.status})`);
  }
  if (!isSuccessEnvelope(parsed)) {
    throw new Error(wireError ?? 'Funds rejected the payout account state change');
  }
};
